import type {
  ClassPublication,
  LiveBoardRow,
  LiveBoardSession,
  SimulatorAppManifest,
  TicketTemplateContentSnapshot,
  TicketTemplate,
  TicketVersion,
  TicketWorkspaceItem,
  V2WorkspaceSnapshot
} from "../domain/index.js";
import { buildStudentWorkspaceSnapshot, publishTicketVersion } from "../domain/index.js";

export const demoTickets: TicketWorkspaceItem[] = [
  {
    id: "ticket-1842",
    number: "1842",
    title: "DNS-probleem na domeinjoin oplossen",
    description:
      "Een medewerker kan na het aanmelden op de werkplek geen interne portal openen. Controleer DNS, domeinlidmaatschap en netwerkconfiguratie voordat je terugkoppelt.",
    status: "in_progress",
    priority: "hoog",
    type: "Game",
    topic: "Networking",
    complexity: "Intermediate",
    environment: "Hybrid",
    dueLabel: "Vandaag 14:30",
    reporter: "M. de Vries",
    teacher: "A. Kazan",
    targets: [
      {
        id: "dns-client",
        title: "Client gebruikt interne DNS",
        description: "Controleer de DNS-instelling op de client.",
        visibility: "visible",
        weight: 1,
        evaluatorKey: "network.client_dns",
        scoring: "automatic",
        expected: { dns: "192.168.10.10" }
      },
      {
        id: "domain-login",
        title: "Domeingebruiker kan aanmelden",
        safeTitle: "Verborgen domeincontrole behaald",
        visibility: "hidden_until_completed",
        weight: 2,
        evaluatorKey: "identity.domain_login",
        scoring: "automatic",
        expected: { loginMode: "domain" }
      },
      {
        id: "student-report",
        title: "Servicedeskverslag is volledig",
        visibility: "teacher_only",
        weight: 1,
        evaluatorKey: "manual.report_quality",
        scoring: "manual"
      }
    ],
    results: [
      { targetId: "dns-client", status: "passed", completedAt: "2026-04-21T10:12:00.000Z" }
    ]
  },
  {
    id: "ticket-1788",
    number: "1788",
    title: "Nieuwe collega krijgt basisaccount",
    description:
      "Maak het gevraagde account aan, koppel de juiste groep en noteer welke controle je hebt uitgevoerd.",
    status: "open",
    priority: "gemiddeld",
    type: "Servicedesk",
    topic: "Identity",
    complexity: "Basic",
    environment: "Client",
    dueLabel: "Morgen",
    reporter: "HR servicedesk",
    teacher: "A. Kazan",
    targets: [
      {
        id: "local-user",
        title: "Lokale gebruiker bestaat",
        description: "Maak het lokale account aan op de client.",
        visibility: "visible",
        weight: 1,
        evaluatorKey: "identity.local_user",
        scoring: "automatic"
      },
      {
        id: "group-membership",
        title: "Groepslidmaatschap klopt",
        visibility: "hidden_counted",
        weight: 1,
        evaluatorKey: "identity.local_group_member",
        scoring: "automatic",
        expected: { group: "Werkplek-Beheer" }
      }
    ],
    results: []
  }
];

export const teacherTemplate: TicketTemplate = {
  id: "template-game-dns",
  institutionId: "talland",
  ownerUserId: "teacher-a-kazan",
  title: "DNS-probleem na domeinjoin oplossen",
  description: "Klassikale game opdracht voor netwerkdiagnose en servicedeskcommunicatie.",
  topic: "networking",
  subtopic: "DNS",
  complexity: "intermediate",
  category: "game",
  environment: "hybrid",
  skills: ["diagnose", "dns", "domain join", "rapportage"],
  learningObjectives: [
    "DNS oorzaak vaststellen",
    "Domeinlogin valideren",
    "Servicedeskterugkoppeling schrijven"
  ],
  status: "draft"
};

export const activePublication: ClassPublication = {
  id: "publication-2026-04-21-a",
  ticketVersionId: "version-game-dns-3",
  classGroupId: "ict-n4a",
  publishedByUserId: "teacher-a-kazan",
  availableFrom: "2026-04-21T09:00:00.000Z",
  availableUntil: "2026-04-21T14:30:00.000Z",
  attemptLimit: 1,
  feedbackMode: "teacher_released",
  status: "active"
};

export const liveBoardSession: LiveBoardSession = {
  id: "board-ict-n4a",
  publicationId: activePublication.id,
  openedByUserId: "teacher-a-kazan",
  privacyMode: "initials",
  showHiddenTargetCounts: true,
  showScores: false
};

export const startCriteria = {
  machineProfile: "hybrid-domain",
  brokenDns: true,
  userCanLoginLocally: true
};

export const assessment = {
  automaticWeight: 0.75,
  manualReportWeight: 0.25,
  requiresTeacherRelease: true
};

export const latestTicketVersion: TicketVersion = publishTicketVersion({
  id: "version-game-dns-3",
  template: teacherTemplate,
  versionNumber: 3,
  targets: demoTickets[0]?.targets ?? [],
  startCriteria,
  assessment,
  publishedByUserId: "teacher-a-kazan",
  publishedAt: "2026-04-21T09:00:00.000Z"
});

export const ticketContentSnapshot: TicketTemplateContentSnapshot = {
  template: teacherTemplate,
  editableTargets: demoTickets[0]?.targets ?? [],
  latestVersion: latestTicketVersion,
  activePublications: [activePublication],
  startCriteria,
  assessment,
  guardrails: [
    "Edits aan gepubliceerde content maken een nieuwe immutable version.",
    "Student attempts blijven gekoppeld aan de snapshot waarmee ze gestart zijn.",
    "Hidden target details worden alleen aan docent/admin views geleverd."
  ]
};

export const liveBoardRows: LiveBoardRow[] = [
  {
    attemptId: "attempt-student-1",
    ticketNumber: "151",
    ticketTitle: "Ticket 151 - DNS pad en domain join herstellen",
    studentName: "N. Jansen",
    status: "Bezig",
    completedTargets: 2,
    totalTargets: 4,
    hiddenCompleted: 1,
    scoreLabel: "Verborgen",
    lastActivity: "1 min geleden"
  },
  {
    attemptId: "attempt-student-2",
    ticketNumber: "152",
    ticketTitle: "Ticket 152 - DHCP scope met reservering voor balieclient",
    studentName: "R. Smit",
    status: "Ingeleverd",
    completedTargets: 4,
    totalTargets: 4,
    hiddenCompleted: 2,
    scoreLabel: "Wacht op review",
    lastActivity: "6 min geleden"
  },
  {
    attemptId: "attempt-student-3",
    ticketNumber: "154",
    ticketTitle: "Ticket 154 - GPO hardening en security policy check",
    studentName: "L. Kaya",
    status: "Bezig",
    completedTargets: 1,
    totalTargets: 4,
    hiddenCompleted: 0,
    scoreLabel: "Verborgen",
    lastActivity: "Nu actief"
  },
  {
    attemptId: "attempt-student-4",
    ticketNumber: "156",
    ticketTitle: "Ticket 156 - Firewall en RDP toegangspad",
    studentName: "M. Bakker",
    status: "Nog niet gestart",
    completedTargets: 0,
    totalTargets: 4,
    hiddenCompleted: 0,
    scoreLabel: "Verborgen",
    lastActivity: "Geen activiteit"
  }
];

const fase10AppDefinitions = [
  ["calc", "Rekenmachine", "productivity", "client.utilities"],
  ["paint", "Paint", "productivity", "client.utilities"],
  ["snippingTool", "Knipprogramma", "productivity", "client.evidence"],
  ["regedit", "Register-editor", "admin", "client.registry"],
  ["credentialManager", "Credential Manager", "security", "client.credentials"],
  ["certmgr", "Certificate Manager", "security", "client.certificates"],
  ["certlm", "Certificates - Local Computer", "security", "client.certificates"],
  ["remoteDesktop", "Remote Desktop Connection", "network", "client.remoteAccess"],
  ["clipboardHistory", "Clipboard History", "productivity", "client.clipboard"],
  ["diskCleanup", "Schijfopruiming", "productivity", "client.maintenance"],
  ["systemRestore", "Systeemherstel", "admin", "client.restore"],
  ["reliabilityMonitor", "Betrouwbaarheidscontrole", "admin", "client.diagnostics"],
  ["resourceMonitor", "Resource Monitor", "admin", "client.diagnostics"],
  ["adSites", "Active Directory Sites and Services", "server", "server.activeDirectory"],
  ["adTrusts", "Active Directory Domains and Trusts", "server", "server.activeDirectory"],
  ["certAuthority", "Certification Authority", "server", "server.certificates"],
  ["iisManager", "IIS Manager", "server", "server.web"],
  ["hyperVManager", "Hyper-V Manager", "server", "server.virtualization"],
  ["printManagement", "Print Management", "server", "server.printing"],
  ["dfsManagement", "DFS Management", "server", "server.files"],
  ["npsManager", "Network Policy Server", "server", "server.networkPolicy"],
  ["windowsServerBackup", "Windows Server Backup", "server", "server.backup"],
  ["failoverCluster", "Failover Cluster Manager", "server", "server.cluster"],
  ["fsrmManager", "File Server Resource Manager", "server", "server.files"],
  ["storageManager", "Storage Manager", "server", "server.storage"],
  ["entraUsers", "Entra Users", "admin", "m365.identity"],
  ["entraGroups", "Entra Groups", "admin", "m365.identity"],
  ["entraDevices", "Entra Devices", "admin", "m365.devices"],
  ["entraRoles", "Entra Roles", "security", "m365.roles"],
  ["conditionalAccess", "Conditional Access", "security", "m365.access"],
  ["authMethods", "Authentication Methods", "security", "m365.authentication"],
  ["enterpriseApps", "Enterprise Apps", "admin", "m365.apps"],
  ["intuneDevices", "Intune Devices", "admin", "m365.intune"],
  ["compliancePolicies", "Compliance Policies", "security", "m365.compliance"],
  ["configurationProfiles", "Configuration Profiles", "admin", "m365.intune"],
  ["m365Users", "Microsoft 365 Users", "admin", "m365.users"],
  ["m365Licenses", "Microsoft 365 Licenses", "admin", "m365.licensing"],
  ["defenderPortal", "Defender Portal", "security", "m365.security"]
] satisfies Array<[string, string, SimulatorAppManifest["category"], string]>;

const manifestReadinessOverrides = new Set([
  "remoteDesktop",
  "calc",
  "paint",
  "snippingTool",
  "clipboardHistory",
  "regedit",
  "credentialManager",
  "certmgr",
  "certlm",
  "diskCleanup",
  "systemRestore",
  "reliabilityMonitor",
  "resourceMonitor",
  "adSites",
  "adTrusts",
  "certAuthority",
  "iisManager",
  "hyperVManager",
  "printManagement",
  "dfsManagement",
  "npsManager",
  "windowsServerBackup",
  "failoverCluster",
  "fsrmManager",
  "storageManager",
  "entraUsers",
  "entraGroups",
  "entraDevices",
  "entraRoles",
  "conditionalAccess",
  "authMethods",
  "enterpriseApps",
  "intuneDevices",
  "compliancePolicies",
  "configurationProfiles",
  "m365Users",
  "m365Licenses",
  "defenderPortal"
]);

function buildFase10ReadinessManifests(): SimulatorAppManifest[] {
  return fase10AppDefinitions
    .filter(([appId]) => !manifestReadinessOverrides.has(appId))
    .map(([appId, name, category, capabilityRoot]) => ({
      appId,
      name,
      category,
    capabilities: [`${capabilityRoot}.read`, `${capabilityRoot}.launch`, `${capabilityRoot}.check`],
    stateReads: ["simulator.session", "simulator.machine", capabilityRoot],
    stateWrites: ["simulator.events"],
    commands: [
      {
        commandType: "OPEN_APP",
        capability: `${capabilityRoot}.launch`,
        payloadSchemaKey: "simulator.openApp"
      },
      {
        commandType: "RECORD_APP_CHECK",
        capability: `${capabilityRoot}.check`,
        payloadSchemaKey: "simulator.recordCheck"
      }
    ],
    eventsEmitted: [`simulator.${appId}.opened`, `simulator.${appId}.checked`],
    targetBindings: [`simulator.${appId}.ready`],
      dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
    }));
}

const fase10CloudManifestConfigs = [
  {
    appId: "entraUsers",
    name: "Entra Users",
    category: "admin" as const,
    capabilities: ["cloud.entra.user.write", "cloud.entra.user.read", "m365.identity.launch", "m365.identity.check"],
    stateReads: ["cloud.entra.users"],
    stateWrites: ["cloud.entra.users", "events"],
    commands: [
      { commandType: "ENTRA_USER_CREATE", capability: "cloud.entra.user.write", payloadSchemaKey: "cloud.entraUserCreate" },
      { commandType: "ENTRA_USER_DELETE", capability: "cloud.entra.user.write", payloadSchemaKey: "cloud.entraUserDelete" },
      { commandType: "ENTRA_USER_RESET_PASSWORD", capability: "cloud.entra.user.write", payloadSchemaKey: "cloud.entraUserResetPassword" }
    ]
  },
  {
    appId: "entraGroups",
    name: "Entra Groups",
    category: "admin" as const,
    capabilities: ["cloud.entra.group.write", "cloud.entra.group.read", "m365.identity.launch", "m365.identity.check"],
    stateReads: ["cloud.entra.groups"],
    stateWrites: ["cloud.entra.groups", "events"],
    commands: [
      { commandType: "ENTRA_GROUP_CREATE", capability: "cloud.entra.group.write", payloadSchemaKey: "cloud.entraGroupCreate" },
      { commandType: "ENTRA_GROUP_ADD_MEMBER", capability: "cloud.entra.group.write", payloadSchemaKey: "cloud.entraGroupAddMember" }
    ]
  },
  {
    appId: "entraDevices",
    name: "Entra Devices",
    category: "admin" as const,
    capabilities: ["cloud.entra.device.write", "cloud.entra.device.read", "m365.devices.launch", "m365.devices.check"],
    stateReads: ["cloud.entra.devices"],
    stateWrites: ["cloud.entra.devices", "events"],
    commands: [
      { commandType: "ENTRA_DEVICE_REGISTER", capability: "cloud.entra.device.write", payloadSchemaKey: "cloud.entraDeviceRegister" },
      { commandType: "ENTRA_DEVICE_REVOKE", capability: "cloud.entra.device.write", payloadSchemaKey: "cloud.entraDeviceRevoke" }
    ]
  },
  {
    appId: "entraRoles",
    name: "Entra Roles",
    category: "security" as const,
    capabilities: ["cloud.entra.role.write", "cloud.entra.role.read", "m365.roles.launch", "m365.roles.check"],
    stateReads: ["cloud.entra.roleAssignments"],
    stateWrites: ["cloud.entra.roleAssignments", "events"],
    commands: [
      { commandType: "ENTRA_ROLE_ASSIGN", capability: "cloud.entra.role.write", payloadSchemaKey: "cloud.entraRoleAssign" },
      { commandType: "ENTRA_ROLE_REMOVE", capability: "cloud.entra.role.write", payloadSchemaKey: "cloud.entraRoleRemove" }
    ]
  },
  {
    appId: "conditionalAccess",
    name: "Conditional Access",
    category: "security" as const,
    capabilities: ["cloud.entra.policy.write", "cloud.entra.policy.read", "m365.access.launch", "m365.access.check"],
    stateReads: ["cloud.entra.conditionalAccess"],
    stateWrites: ["cloud.entra.conditionalAccess", "events"],
    commands: [
      { commandType: "CA_POLICY_CREATE", capability: "cloud.entra.policy.write", payloadSchemaKey: "cloud.caPolicyCreate" },
      { commandType: "CA_POLICY_TOGGLE", capability: "cloud.entra.policy.write", payloadSchemaKey: "cloud.caPolicyToggle" }
    ]
  },
  {
    appId: "authMethods",
    name: "Authentication Methods",
    category: "security" as const,
    capabilities: ["cloud.entra.auth.write", "cloud.entra.auth.read", "m365.authentication.launch", "m365.authentication.check"],
    stateReads: ["cloud.entra.authMethods"],
    stateWrites: ["cloud.entra.authMethods", "events"],
    commands: [
      { commandType: "AUTH_METHOD_ENABLE", capability: "cloud.entra.auth.write", payloadSchemaKey: "cloud.authMethodEnable" },
      { commandType: "AUTH_METHOD_REGISTER_FOR_USER", capability: "cloud.entra.auth.write", payloadSchemaKey: "cloud.authMethodRegisterForUser" }
    ]
  },
  {
    appId: "enterpriseApps",
    name: "Enterprise Apps",
    category: "admin" as const,
    capabilities: ["cloud.entra.app.write", "cloud.entra.app.read", "m365.apps.launch", "m365.apps.check"],
    stateReads: ["cloud.entra.enterpriseApps"],
    stateWrites: ["cloud.entra.enterpriseApps", "events"],
    commands: [
      { commandType: "ENTERPRISE_APP_REGISTER", capability: "cloud.entra.app.write", payloadSchemaKey: "cloud.enterpriseAppRegister" },
      { commandType: "ENTERPRISE_APP_GRANT_CONSENT", capability: "cloud.entra.app.write", payloadSchemaKey: "cloud.enterpriseAppGrantConsent" }
    ]
  },
  {
    appId: "intuneDevices",
    name: "Intune Devices",
    category: "admin" as const,
    capabilities: ["cloud.intune.device.write", "cloud.intune.device.read", "m365.intune.launch", "m365.intune.check"],
    stateReads: ["cloud.intune.devices"],
    stateWrites: ["cloud.intune.devices", "events"],
    commands: [
      { commandType: "INTUNE_DEVICE_ENROLL", capability: "cloud.intune.device.write", payloadSchemaKey: "cloud.intuneDeviceEnroll" },
      { commandType: "INTUNE_DEVICE_RETIRE", capability: "cloud.intune.device.write", payloadSchemaKey: "cloud.intuneDeviceRetire" }
    ]
  },
  {
    appId: "compliancePolicies",
    name: "Compliance Policies",
    category: "security" as const,
    capabilities: ["cloud.intune.compliance.write", "cloud.intune.compliance.read", "m365.compliance.launch", "m365.compliance.check"],
    stateReads: ["cloud.intune.compliance"],
    stateWrites: ["cloud.intune.compliance", "events"],
    commands: [
      { commandType: "COMPLIANCE_POLICY_CREATE", capability: "cloud.intune.compliance.write", payloadSchemaKey: "cloud.compliancePolicyCreate" },
      { commandType: "COMPLIANCE_POLICY_ASSIGN", capability: "cloud.intune.compliance.write", payloadSchemaKey: "cloud.compliancePolicyAssign" }
    ]
  },
  {
    appId: "configurationProfiles",
    name: "Configuration Profiles",
    category: "admin" as const,
    capabilities: ["cloud.intune.config.write", "cloud.intune.config.read", "m365.intune.launch", "m365.intune.check"],
    stateReads: ["cloud.intune.configProfiles"],
    stateWrites: ["cloud.intune.configProfiles", "events"],
    commands: [
      { commandType: "CONFIG_PROFILE_CREATE", capability: "cloud.intune.config.write", payloadSchemaKey: "cloud.configProfileCreate" },
      { commandType: "CONFIG_PROFILE_ASSIGN", capability: "cloud.intune.config.write", payloadSchemaKey: "cloud.configProfileAssign" }
    ]
  },
  {
    appId: "m365Users",
    name: "Microsoft 365 Users",
    category: "admin" as const,
    capabilities: ["cloud.m365.user.write", "cloud.m365.user.read", "m365.users.launch", "m365.users.check"],
    stateReads: ["cloud.m365.users"],
    stateWrites: ["cloud.m365.users", "events"],
    commands: [
      { commandType: "M365_USER_LICENSE_ASSIGN", capability: "cloud.m365.user.write", payloadSchemaKey: "cloud.m365UserLicenseAssign" }
    ]
  },
  {
    appId: "m365Licenses",
    name: "Microsoft 365 Licenses",
    category: "admin" as const,
    capabilities: ["cloud.m365.license.write", "cloud.m365.license.read", "m365.licensing.launch", "m365.licensing.check"],
    stateReads: ["cloud.m365.licenses"],
    stateWrites: ["cloud.m365.licenses", "events"],
    commands: [
      { commandType: "M365_LICENSE_PURCHASE", capability: "cloud.m365.license.write", payloadSchemaKey: "cloud.m365LicensePurchase" },
      { commandType: "M365_LICENSE_REVOKE", capability: "cloud.m365.license.write", payloadSchemaKey: "cloud.m365LicenseRevoke" }
    ]
  },
  {
    appId: "defenderPortal",
    name: "Defender Portal",
    category: "security" as const,
    capabilities: ["cloud.defender.write", "cloud.defender.read", "m365.security.launch", "m365.security.check"],
    stateReads: ["cloud.defenderPortal.incidents"],
    stateWrites: ["cloud.defenderPortal.incidents", "events"],
    commands: [
      { commandType: "DEFENDER_INCIDENT_RESOLVE", capability: "cloud.defender.write", payloadSchemaKey: "cloud.defenderIncidentResolve" },
      { commandType: "DEFENDER_ALERT_INVESTIGATE", capability: "cloud.defender.write", payloadSchemaKey: "cloud.defenderAlertInvestigate" }
    ]
  }
] satisfies Array<{
  appId: string;
  name: string;
  category: SimulatorAppManifest["category"];
  capabilities: string[];
  stateReads: string[];
  stateWrites: string[];
  commands: Array<{ commandType: string; capability: string; payloadSchemaKey: string }>;
}>;

function buildFase10CloudManifests(): SimulatorAppManifest[] {
  return fase10CloudManifestConfigs.map((config) => ({
    appId: config.appId,
    name: config.name,
    category: config.category,
    capabilities: [...config.capabilities],
    stateReads: [...config.stateReads],
    stateWrites: [...config.stateWrites],
    commands: [
      { commandType: "OPEN_APP", capability: config.capabilities.find((capability) => capability.endsWith(".launch")) ?? "simulator.open", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: config.capabilities.find((capability) => capability.endsWith(".check")) ?? "simulator.check", payloadSchemaKey: "simulator.recordCheck" },
      ...config.commands
    ],
    eventsEmitted: [
      `simulator.${config.appId}.opened`,
      `simulator.${config.appId}.checked`,
      ...config.commands.map((command) => `simulator.${config.appId}.${command.commandType.toLowerCase()}`)
    ],
    targetBindings: [`simulator.${config.appId}.ready`],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  }));
}

export const simulatorApps: SimulatorAppManifest[] = [
  {
    appId: "aduc",
    name: "Active Directory Users and Computers",
    category: "admin",
    capabilities: [
      "domainUsers.read",
      "domainUsers.write",
      "domainUsers.state.write",
      "domainUsers.password.write",
      "domainGroups.read",
      "domainGroups.write",
      "domainOu.write",
      "domainComputers.read",
      "domainComputers.write"
    ],
    stateReads: [
      "server.activeDirectory.users",
      "server.activeDirectory.groups",
      "server.activeDirectory.organizationalUnits",
      "server.activeDirectory.computers"
    ],
    stateWrites: [
      "server.activeDirectory.users",
      "server.activeDirectory.groups",
      "server.activeDirectory.organizationalUnits",
      "server.activeDirectory.computers"
    ],
    commands: [
      {
        commandType: "LOGIN_ATTEMPT",
        capability: "domainUsers.read",
        payloadSchemaKey: "client.loginAttempt"
      },
      {
        commandType: "SERVER_CREATE_OU",
        capability: "domainOu.write",
        payloadSchemaKey: "server.createOrganizationalUnit"
      },
      {
        commandType: "SERVER_CREATE_DOMAIN_USER",
        capability: "domainUsers.write",
        payloadSchemaKey: "server.createDomainUser"
      },
      {
        commandType: "SERVER_CREATE_DOMAIN_GROUP",
        capability: "domainGroups.write",
        payloadSchemaKey: "server.createDomainGroup"
      },
      {
        commandType: "SERVER_ADD_DOMAIN_GROUP_MEMBER",
        capability: "domainGroups.write",
        payloadSchemaKey: "server.addDomainGroupMember"
      },
      {
        commandType: "SERVER_SET_DOMAIN_USER_STATE",
        capability: "domainUsers.state.write",
        payloadSchemaKey: "server.setDomainUserState"
      },
      {
        commandType: "SERVER_RESET_DOMAIN_USER_PASSWORD",
        capability: "domainUsers.password.write",
        payloadSchemaKey: "server.resetDomainUserPassword"
      },
      {
        commandType: "SERVER_CREATE_DOMAIN_COMPUTER",
        capability: "domainComputers.write",
        payloadSchemaKey: "server.createDomainComputer"
      },
      {
        commandType: "SERVER_UPDATE_DOMAIN_COMPUTER",
        capability: "domainComputers.write",
        payloadSchemaKey: "server.updateDomainComputer"
      },
      {
        commandType: "SERVER_MOVE_AD_OBJECT",
        capability: "domainOu.write",
        payloadSchemaKey: "server.moveActiveDirectoryObject"
      },
      {
        commandType: "SERVER_DELETE_AD_OBJECT",
        capability: "domainOu.write",
        payloadSchemaKey: "server.deleteActiveDirectoryObject"
      },
      {
        commandType: "SERVER_UNLOCK_DOMAIN_USER",
        capability: "domainUsers.state.write",
        payloadSchemaKey: "server.unlockDomainUser"
      },
      {
        commandType: "SERVER_SET_ACCOUNT_EXPIRY",
        capability: "domainUsers.state.write",
        payloadSchemaKey: "server.setAccountExpiry"
      }
    ],
    eventsEmitted: [
      "simulator.domain_ou.created",
      "simulator.domain_user.created",
      "simulator.domain_group.created",
      "simulator.domain_group.membership.updated",
      "simulator.domain_user.state.updated",
      "simulator.domain_user.password.reset",
      "simulator.domain_computer.saved",
      "simulator.domain_object.moved",
      "simulator.domain_object.deleted",
      "simulator.domain_user.unlocked",
      "simulator.domain_user.account_options.updated"
    ],
    targetBindings: [
      "identity.organizational_unit_exists",
      "identity.domain_user_exists",
      "identity.domain_group_exists",
      "identity.domain_group_membership",
      "identity.domain_user_enabled_matches",
      "identity.domain_password_reset_recorded"
    ],
    dependencies: ["simulator-core.windowing", "simulator-domain.activeDirectory"]
  },
  {
    appId: "dns-manager",
    name: "DNS Manager",
    category: "network",
    capabilities: ["dns.zone.read", "dns.zone.write", "dns.record.write", "dns.resolve.read"],
    stateReads: ["server.dns.zones", "network.adapters", "network.clientDns", "network.reachability"],
    stateWrites: ["server.dns.zones"],
    commands: [
      {
        commandType: "CLIENT_SET_DNS",
        capability: "dns.zone.read",
        payloadSchemaKey: "client.setDns"
      },
      {
        commandType: "SERVER_DNS_CREATE_ZONE",
        capability: "dns.zone.write",
        payloadSchemaKey: "server.dnsZone"
      },
      {
        commandType: "SERVER_DNS_ADD_RECORD",
        capability: "dns.record.write",
        payloadSchemaKey: "server.dnsRecord"
      },
      {
        commandType: "SERVER_DNS_UPDATE_RECORD",
        capability: "dns.record.write",
        payloadSchemaKey: "server.dnsRecordUpdate"
      },
      {
        commandType: "SERVER_DNS_DELETE_RECORD",
        capability: "dns.record.write",
        payloadSchemaKey: "server.dnsDeleteRecord"
      },
      {
        commandType: "SERVER_DNS_DELETE_ZONE",
        capability: "dns.zone.write",
        payloadSchemaKey: "server.dnsDeleteZone"
      },
      {
        commandType: "SERVER_DNS_SET_FORWARDERS",
        capability: "dns.zone.write",
        payloadSchemaKey: "server.dnsForwarders"
      }
    ],
    eventsEmitted: [
      "simulator.dns.zone.created",
      "simulator.dns.record.created",
      "simulator.dns.record.updated",
      "simulator.dns.record.deleted",
      "simulator.dns.zone.deleted",
      "simulator.dns.forwarders.updated"
    ],
    targetBindings: ["network.dns_zone_exists", "network.dns_record_exists", "network.client_dns_resolves"],
    dependencies: ["simulator-core.windowing", "simulator-domain.dns"]
  },
  {
    appId: "dhcpManager",
    name: "DHCP Manager",
    category: "server",
    capabilities: [
      "dhcp.scope.read",
      "dhcp.scope.write",
      "dhcp.lease.read",
      "dhcp.reservation.write",
      "dhcp.option.write"
    ],
    stateReads: ["server.dhcp.scopes", "server.dhcp.leases", "network.adapters"],
    stateWrites: ["server.dhcp.scopes", "server.dhcp.scopes[].reservations", "server.dhcp.scopes[].options"],
    commands: [
      {
        commandType: "DHCP_CREATE_SCOPE",
        capability: "dhcp.scope.write",
        payloadSchemaKey: "server.dhcpScope"
      },
      {
        commandType: "DHCP_SET_OPTION",
        capability: "dhcp.option.write",
        payloadSchemaKey: "server.dhcpOption"
      },
      {
        commandType: "DHCP_ADD_RESERVATION",
        capability: "dhcp.reservation.write",
        payloadSchemaKey: "server.dhcpReservation"
      },
      {
        commandType: "DHCP_DELETE_SCOPE",
        capability: "dhcp.scope.write",
        payloadSchemaKey: "server.dhcpDeleteScope"
      },
      {
        commandType: "DHCP_DELETE_RESERVATION",
        capability: "dhcp.reservation.write",
        payloadSchemaKey: "server.dhcpDeleteReservation"
      }
    ],
    eventsEmitted: [
      "simulator.dhcp.scope.saved",
      "simulator.dhcp.option.updated",
      "simulator.dhcp.reservation.saved",
      "simulator.dhcp.scope.deleted",
      "simulator.dhcp.reservation.deleted"
    ],
    targetBindings: [
      "network.dhcp_scope_present",
      "network.dhcp_option_router_dns_configured",
      "network.dhcp_reservation_present",
      "network.dhcp_lease_visible"
    ],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus", "simulator-domain.dhcp"]
  },
  {
    appId: "netSettings",
    name: "Network & Internet Settings",
    category: "network",
    capabilities: [
      "network.adapter.read",
      "network.adapter.write",
      "network.dnsClient.write",
      "network.dhcp.write",
      "network.lease.read"
    ],
    stateReads: ["network.adapters", "network.clientDns", "server.dhcp.scopes", "server.dhcp.leases"],
    stateWrites: ["network.adapters", "network.clientDns", "server.dhcp.leases", "network.reachability"],
    commands: [
      {
        commandType: "NETWORK_SET_ADAPTER_CONFIG",
        capability: "network.adapter.write",
        payloadSchemaKey: "client.networkAdapterConfig"
      },
      {
        commandType: "NETWORK_SET_DNS_SERVER",
        capability: "network.dnsClient.write",
        payloadSchemaKey: "client.networkDnsConfig"
      },
      {
        commandType: "NETWORK_ENABLE_DHCP",
        capability: "network.dhcp.write",
        payloadSchemaKey: "client.networkDhcp"
      },
      {
        commandType: "NETWORK_RELEASE_RENEW_LEASE",
        capability: "network.dhcp.write",
        payloadSchemaKey: "client.networkDhcpRenew"
      }
    ],
    eventsEmitted: [
      "simulator.network.adapter.updated",
      "simulator.network.dns.updated",
      "simulator.network.lease.renewed"
    ],
    targetBindings: [
      "network.adapter_dns_matches",
      "network.adapter_dhcp_enabled",
      "network.adapter_lease_present",
      "network.domain_controller_reachable"
    ],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus", "simulator-domain.network"]
  },
  {
    appId: "sysdm",
    name: "System Properties",
    category: "admin",
    capabilities: [
      "system.identity.read",
      "system.identity.write",
      "domain.membership.read",
      "domain.membership.write",
      "domain.computer.write",
      "remoteDesktop.write",
      "local.system.environment.write"
    ],
    stateReads: ["system.hostname", "system.domainMembership", "network.adapters", "server.activeDirectory.computers", "remoteDesktop", "system.preferences"],
    stateWrites: ["system.hostname", "system.domainMembership", "server.activeDirectory.computers", "network.reachability", "remoteDesktop", "system.preferences"],
    commands: [
      {
        commandType: "SYSTEM_RENAME_COMPUTER",
        capability: "system.identity.write",
        payloadSchemaKey: "client.systemRename"
      },
      {
        commandType: "SYSTEM_JOIN_DOMAIN",
        capability: "domain.membership.write",
        payloadSchemaKey: "client.domainJoin"
      },
      {
        commandType: "SYSTEM_LEAVE_DOMAIN",
        capability: "domain.membership.write",
        payloadSchemaKey: "client.domainLeave"
      },
      {
        commandType: "RDP_ENABLE",
        capability: "remoteDesktop.write",
        payloadSchemaKey: "client.remoteDesktopEnable"
      },
      {
        commandType: "SYSTEM_SET_ENVIRONMENT_VARIABLE",
        capability: "local.system.environment.write",
        payloadSchemaKey: "local.systemEnvironmentVariable"
      }
    ],
    eventsEmitted: [
      "simulator.system.hostname.updated",
      "simulator.system.domain.joined",
      "simulator.system.domain.left",
      "simulator.remoteDesktop.updated",
      "simulator.system.environment.updated"
    ],
    targetBindings: [
      "identity.computer_name_matches",
      "identity.domain_joined",
      "identity.domain_name_matches",
      "identity.domain_computer_exists",
      "remoteDesktop.enabled_matches"
    ],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus", "simulator-domain.identity"]
  },
  {
    appId: "gpmc",
    name: "Group Policy Management",
    category: "server",
    capabilities: [
      "policy.gpo.read",
      "policy.gpo.write",
      "policy.gpo.link.write",
      "policy.gpo.setting.write",
      "policy.gpo.scope.read"
    ],
    stateReads: [
      "server.activeDirectory.groupPolicies",
      "server.activeDirectory.organizationalUnits",
      "server.activeDirectory.users",
      "server.activeDirectory.computers",
      "server.activeDirectory.groups",
      "server.activeDirectory.blockInheritanceTargets"
    ],
    stateWrites: ["server.activeDirectory.groupPolicies"],
    commands: [
      {
        commandType: "GPO_CREATE",
        capability: "policy.gpo.write",
        payloadSchemaKey: "server.gpoCreate"
      },
      {
        commandType: "GPO_LINK_OU",
        capability: "policy.gpo.link.write",
        payloadSchemaKey: "server.gpoLink"
      },
      {
        commandType: "GPO_SET_SETTING",
        capability: "policy.gpo.setting.write",
        payloadSchemaKey: "server.gpoSetting"
      },
      {
        commandType: "GPO_ENFORCE_LINK",
        capability: "policy.gpo.link.write",
        payloadSchemaKey: "server.gpoEnforce"
      },
      {
        commandType: "GPO_UNLINK_OU",
        capability: "policy.gpo.link.write",
        payloadSchemaKey: "server.gpoUnlink"
      },
      {
        commandType: "GPO_BLOCK_INHERITANCE",
        capability: "policy.gpo.link.write",
        payloadSchemaKey: "server.gpoBlockInheritance"
      }
    ],
    eventsEmitted: [
      "simulator.gpo.created",
      "simulator.gpo.link.updated",
      "simulator.gpo.setting.updated",
      "simulator.gpo.link.enforced",
      "simulator.gpo.link.removed",
      "simulator.gpo.inheritance.updated"
    ],
    targetBindings: [
      "policy.gpo_exists",
      "policy.gpo_linked_to_target",
      "policy.gpo_setting_matches",
      "policy.gpo_link_enforced",
      "policy.gpo_scope_matches"
    ],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus", "simulator-domain.activeDirectory"]
  },
  {
    appId: "explorer",
    name: "File Explorer",
    category: "productivity",
    capabilities: [
      "file.folder.read",
      "file.folder.write",
      "file.file.read",
      "file.file.write",
      "file.share.read",
      "file.share.write",
      "file.permission.read",
      "file.permission.write",
      "network.drive.write"
    ],
    stateReads: ["filesystem.nodes", "filesystem.rootId", "shares", "network.drives"],
    stateWrites: ["filesystem.nodes", "filesystem.nodes[].permissions", "shares", "network.drives"],
    commands: [
      {
        commandType: "FILE_CREATE_FOLDER",
        capability: "file.folder.write",
        payloadSchemaKey: "client.fileCreateFolder"
      },
      {
        commandType: "FILE_CREATE",
        capability: "file.file.write",
        payloadSchemaKey: "client.fileCreate"
      },
      {
        commandType: "FILE_SET_PERMISSION",
        capability: "file.permission.write",
        payloadSchemaKey: "client.filePermission"
      },
      {
        commandType: "SHARE_CREATE",
        capability: "file.share.write",
        payloadSchemaKey: "client.shareCreate"
      },
      {
        commandType: "SHARE_SET_PERMISSION",
        capability: "file.share.write",
        payloadSchemaKey: "client.sharePermission"
      },
      {
        commandType: "FILE_DELETE_FOLDER",
        capability: "file.folder.write",
        payloadSchemaKey: "client.fileDeleteFolder"
      },
      {
        commandType: "FILE_DELETE",
        capability: "file.file.write",
        payloadSchemaKey: "client.fileDelete"
      },
      {
        commandType: "NETWORK_DRIVE_MAP",
        capability: "network.drive.write",
        payloadSchemaKey: "network.driveMap"
      },
      {
        commandType: "NETWORK_DRIVE_UNMAP",
        capability: "network.drive.write",
        payloadSchemaKey: "network.driveUnmap"
      }
    ],
    eventsEmitted: [
      "simulator.file.folder.created",
      "simulator.file.created",
      "simulator.file.permissions.updated",
      "simulator.file.share.created",
      "simulator.file.share.permissions.updated",
      "simulator.file.folder.deleted",
      "simulator.file.deleted",
      "simulator.network.drive.mapped",
      "simulator.network.drive.unmapped"
    ],
    targetBindings: [
      "file.folder_exists",
      "file.share_exists",
      "file.acl_principal_has_permission",
      "file.acl_forbidden_permission_absent",
      "network.drive_mapped"
    ],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus", "simulator-domain.filesystem"]
  },
  {
    appId: "services",
    name: "Services",
    category: "admin",
    capabilities: ["service.read", "service.write"],
    stateReads: ["services"],
    stateWrites: ["services"],
    commands: [
      {
        commandType: "SERVICE_SET_STARTUP_TYPE",
        capability: "service.write",
        payloadSchemaKey: "client.serviceStartup"
      },
      {
        commandType: "SERVICE_START",
        capability: "service.write",
        payloadSchemaKey: "client.serviceStart"
      },
      {
        commandType: "SERVICE_STOP",
        capability: "service.write",
        payloadSchemaKey: "client.serviceStop"
      }
    ],
    eventsEmitted: [
      "simulator.service.started",
      "simulator.service.stopped",
      "simulator.service.startup.updated"
    ],
    targetBindings: ["service.status_matches", "service.startup_type_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus", "simulator-domain.services"]
  },
  {
    appId: "wf",
    name: "Windows Defender Firewall",
    category: "security",
    capabilities: ["firewall.profile.read", "firewall.profile.write", "firewall.rule.read", "firewall.rule.write"],
    stateReads: ["firewall.profiles", "firewall.rules", "remoteDesktop"],
    stateWrites: ["firewall.profiles", "firewall.rules"],
    commands: [
      {
        commandType: "FIREWALL_SET_PROFILE",
        capability: "firewall.profile.write",
        payloadSchemaKey: "client.firewallProfileUpdate"
      },
      {
        commandType: "FIREWALL_ENABLE_RULE",
        capability: "firewall.rule.write",
        payloadSchemaKey: "client.firewallRuleEnable"
      },
      {
        commandType: "FIREWALL_DISABLE_RULE",
        capability: "firewall.rule.write",
        payloadSchemaKey: "client.firewallRuleDisable"
      },
      {
        commandType: "FIREWALL_DELETE_RULE",
        capability: "firewall.rule.write",
        payloadSchemaKey: "client.firewallRuleDelete"
      }
    ],
    eventsEmitted: ["simulator.firewall.profile.updated", "simulator.firewall.rule.enabled", "simulator.firewall.rule.disabled", "simulator.firewall.rule.deleted"],
    targetBindings: ["firewall.rule_enabled_matches", "firewall.profile_enabled_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus", "simulator-domain.firewall"]
  },
  {
    appId: "remoteDesktop",
    name: "Remote Desktop",
    category: "network",
    capabilities: ["remoteDesktop.read", "remoteDesktop.write"],
    stateReads: ["remoteDesktop", "services", "firewall.rules"],
    stateWrites: ["remoteDesktop", "services", "firewall.rules"],
    commands: [
      {
        commandType: "RDP_ENABLE",
        capability: "remoteDesktop.write",
        payloadSchemaKey: "client.remoteDesktopEnable"
      },
      {
        commandType: "RDP_SET_ALLOWED_GROUP",
        capability: "remoteDesktop.write",
        payloadSchemaKey: "client.remoteDesktopAllowedGroup"
      }
    ],
    eventsEmitted: ["simulator.remoteDesktop.updated", "simulator.remoteDesktop.allowedGroup.updated"],
    targetBindings: [
      "remoteDesktop.enabled_matches",
      "remoteDesktop.allowed_group_matches",
      "service.status_matches",
      "firewall.rule_enabled_matches"
    ],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus", "simulator-domain.remoteAccess"]
  },
  {
    appId: "eventvwr",
    name: "Event Viewer",
    category: "admin",
    capabilities: ["audit.event.read", "audit.event.write", "audit.event.check", "audit.view.write"],
    stateReads: ["events", "eventViewer.currentCategory", "eventViewer.filters", "eventViewer.checkedEventIds", "eventViewer.customViews"],
    stateWrites: ["events", "eventViewer.currentCategory", "eventViewer.filters", "eventViewer.checkedEventIds", "eventViewer.customViews"],
    commands: [
      {
        commandType: "AUDIT_RECORD_EVENT",
        capability: "audit.event.write",
        payloadSchemaKey: "server.auditEventRecord"
      },
      {
        commandType: "EVENT_VIEWER_FILTER",
        capability: "audit.event.read",
        payloadSchemaKey: "client.eventViewerFilter"
      },
      {
        commandType: "EVENT_VIEWER_RECORD_CHECK",
        capability: "audit.event.check",
        payloadSchemaKey: "client.eventViewerRecordCheck"
      },
      {
        commandType: "EVENT_VIEWER_CREATE_VIEW",
        capability: "audit.view.write",
        payloadSchemaKey: "client.eventViewerCreateView"
      }
    ],
    eventsEmitted: [
      "simulator.audit.recorded",
      "simulator.eventViewer.filtered",
      "simulator.eventViewer.checked",
      "simulator.eventViewer.viewCreated"
    ],
    targetBindings: ["audit.event_exists", "audit.event_visible", "audit.event_checked"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus", "simulator-domain.audit"]
  },
  {
    appId: "server-manager",
    name: "Server Manager",
    category: "server",
    capabilities: ["serverRoles.read", "serverRoles.write", "serverRoles.remove", "domainController.promote", "domainController.demote"],
    stateReads: ["server.roles", "server.activeDirectory", "server.dns.zones", "server.dhcp.scopes"],
    stateWrites: ["server.roles", "server.activeDirectory", "server.dns.zones"],
    commands: [
      {
        commandType: "SERVER_ROLE_INSTALL",
        capability: "serverRoles.write",
        payloadSchemaKey: "server.installRole"
      },
      {
        commandType: "SERVER_ROLE_UNINSTALL",
        capability: "serverRoles.remove",
        payloadSchemaKey: "server.uninstallRole"
      },
      {
        commandType: "SERVER_ROLE_UNINSTALL",
        capability: "serverRoles.write",
        payloadSchemaKey: "server.uninstallRole"
      },
      {
        commandType: "SERVER_PROMOTE_DOMAIN_CONTROLLER",
        capability: "domainController.promote",
        payloadSchemaKey: "server.promoteDomainController"
      },
      {
        commandType: "SERVER_DC_PROMOTE",
        capability: "serverRoles.write",
        payloadSchemaKey: "server.promoteDomainController"
      },
      {
        commandType: "SERVER_DC_DEMOTE",
        capability: "serverRoles.write",
        payloadSchemaKey: "server.demoteDomainController"
      }
    ],
    eventsEmitted: [
      "simulator.server.roleInstalled",
      "simulator.server.roleRemoved",
      "simulator.server.domainController.promoted",
      "simulator.server.domainController.demoted"
    ],
    targetBindings: ["server.role_installed", "server.role_absent", "identity.domain_controller_promoted"],
    dependencies: ["simulator-core.windowing", "simulator-domain.server"]
  },
  {
    appId: "lusrmgr",
    name: "Lokale gebruikers en groepen",
    category: "admin",
    capabilities: ["local.user.read", "local.user.write", "local.user.password.write", "local.group.read", "local.group.write"],
    stateReads: ["identity.localUsers", "identity.localGroups", "identity.localGroupMembership"],
    stateWrites: ["identity.localUsers", "identity.localGroups", "identity.localGroupMembership", "identity.localUserStates"],
    commands: [
      { commandType: "LOCAL_USER_CREATE", capability: "local.user.write", payloadSchemaKey: "local.createUser" },
      { commandType: "LOCAL_USER_DELETE", capability: "local.user.write", payloadSchemaKey: "local.deleteUser" },
      { commandType: "LOCAL_USER_SET_STATE", capability: "local.user.write", payloadSchemaKey: "local.setUserState" },
      { commandType: "LOCAL_GROUP_CREATE", capability: "local.group.write", payloadSchemaKey: "local.createGroup" },
      { commandType: "LOCAL_GROUP_ADD_MEMBER", capability: "local.group.write", payloadSchemaKey: "local.addGroupMember" },
      { commandType: "LOCAL_USER_RESET_PASSWORD", capability: "local.user.password.write", payloadSchemaKey: "local.resetPassword" }
    ],
    eventsEmitted: ["simulator.local_user.created", "simulator.local_user.deleted", "simulator.local_group.updated"],
    targetBindings: ["identity.local_user", "identity.local_group_member"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "netplwiz",
    name: "Gebruikersaccounts",
    category: "admin",
    capabilities: ["local.user.autologin.write", "local.session.write", "local.group.write"],
    stateReads: ["session.autoLogin", "session.requireCtrlAltDel", "identity.localGroupMembership"],
    stateWrites: ["session.autoLogin", "session.requireCtrlAltDel", "identity.localGroupMembership"],
    commands: [
      { commandType: "LOCAL_USER_AUTOLOGIN_TOGGLE", capability: "local.user.autologin.write", payloadSchemaKey: "local.autoLoginToggle" },
      { commandType: "LOCAL_USER_REQUIRE_CTRL_ALT_DEL", capability: "local.session.write", payloadSchemaKey: "local.requireCtrlAltDel" },
      { commandType: "LOCAL_GROUP_ADD_MEMBER", capability: "local.group.write", payloadSchemaKey: "local.addGroupMember" }
    ],
    eventsEmitted: ["simulator.local_session.updated"],
    targetBindings: ["identity.local_group_member"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "secpol",
    name: "Lokale beveiligingsinstellingen",
    category: "security",
    capabilities: ["local.securityPolicy.read", "local.securityPolicy.write", "local.auditPolicy.write", "local.userRights.write"],
    stateReads: ["system.localPolicies"],
    stateWrites: ["system.localPolicies"],
    commands: [
      { commandType: "SECURITY_POLICY_SET", capability: "local.securityPolicy.write", payloadSchemaKey: "local.securityPolicySet" },
      { commandType: "AUDIT_POLICY_SET", capability: "local.auditPolicy.write", payloadSchemaKey: "local.auditPolicySet" },
      { commandType: "USER_RIGHTS_ASSIGN", capability: "local.userRights.write", payloadSchemaKey: "local.userRightsAssign" }
    ],
    eventsEmitted: ["simulator.security_policy.updated", "simulator.audit_policy.updated"],
    targetBindings: ["policy.gpo_setting_matches", "audit.event_exists"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "uac",
    name: "Gebruikersaccountbeheer",
    category: "security",
    capabilities: ["local.uac.read", "local.uac.write"],
    stateReads: ["system.localPolicies.uac"],
    stateWrites: ["system.localPolicies.uac"],
    commands: [
      { commandType: "UAC_SET_LEVEL", capability: "local.uac.write", payloadSchemaKey: "local.uacSetLevel" },
      { commandType: "UAC_TOGGLE_ADMIN_APPROVAL", capability: "local.uac.write", payloadSchemaKey: "local.uacToggleAdminApproval" }
    ],
    eventsEmitted: ["simulator.uac.updated"],
    targetBindings: ["local.security_policy_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "gpedit",
    name: "Editor voor lokaal groepsbeleid",
    category: "security",
    capabilities: ["local.gpo.setting.read", "local.gpo.setting.write"],
    stateReads: ["system.localPolicies.gpoSettings", "system.localPolicies.securityOptions", "system.localPolicies.auditPolicy"],
    stateWrites: ["system.localPolicies.gpoSettings"],
    commands: [
      { commandType: "LOCAL_GPO_SET_SETTING", capability: "local.gpo.setting.write", payloadSchemaKey: "local.gpoSetSetting" }
    ],
    eventsEmitted: ["simulator.local_gpo.updated"],
    targetBindings: ["local.gpo_setting_matches", "local.explorer_view_setting_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "taskschd",
    name: "Taakplanner",
    category: "admin",
    capabilities: ["local.scheduledTask.read", "local.scheduledTask.write", "local.scheduledTask.execute"],
    stateReads: ["system.scheduledTasks"],
    stateWrites: ["system.scheduledTasks"],
    commands: [
      { commandType: "TASK_CREATE", capability: "local.scheduledTask.write", payloadSchemaKey: "local.taskCreate" },
      { commandType: "TASK_DELETE", capability: "local.scheduledTask.write", payloadSchemaKey: "local.taskDelete" },
      { commandType: "TASK_TOGGLE", capability: "local.scheduledTask.write", payloadSchemaKey: "local.taskToggle" },
      { commandType: "TASK_RUN_NOW", capability: "local.scheduledTask.execute", payloadSchemaKey: "local.taskRunNow" }
    ],
    eventsEmitted: ["simulator.scheduled_task.created", "simulator.scheduled_task.updated", "simulator.scheduled_task.started"],
    targetBindings: ["local.scheduled_task_exists", "local.scheduled_task_state_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "taskmgr",
    name: "Taakbeheer",
    category: "admin",
    capabilities: ["local.process.read", "local.process.write", "local.startup.write", "service.write"],
    stateReads: ["system.processes", "system.startupItems", "server.services"],
    stateWrites: ["system.processes", "system.startupItems", "server.services"],
    commands: [
      { commandType: "PROCESS_END", capability: "local.process.write", payloadSchemaKey: "local.processEnd" },
      { commandType: "SERVICE_START_FROM_TASKMGR", capability: "service.write", payloadSchemaKey: "service.startFromTaskmgr" },
      { commandType: "STARTUP_ITEM_TOGGLE", capability: "local.startup.write", payloadSchemaKey: "local.startupToggle" }
    ],
    eventsEmitted: ["simulator.process.ended", "simulator.startup.updated", "simulator.service.updated"],
    targetBindings: ["local.process_absent", "service.status_matches", "local.startup_item_state_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "devmgmt",
    name: "Apparaatbeheer",
    category: "admin",
    capabilities: ["local.device.read", "local.device.write", "local.device.driver.write"],
    stateReads: ["devices", "system.devices"],
    stateWrites: ["devices", "system.devices"],
    commands: [
      { commandType: "DEVICE_ENABLE", capability: "local.device.write", payloadSchemaKey: "local.deviceEnable" },
      { commandType: "DEVICE_DISABLE", capability: "local.device.write", payloadSchemaKey: "local.deviceDisable" },
      { commandType: "DEVICE_UPDATE_DRIVER", capability: "local.device.driver.write", payloadSchemaKey: "local.deviceDriverUpdate" }
    ],
    eventsEmitted: ["simulator.device.enabled", "simulator.device.disabled", "simulator.device.driver.updated"],
    targetBindings: ["local.device_state_matches", "local.device_driver_version_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "diskmgmt",
    name: "Schijfbeheer",
    category: "admin",
    capabilities: ["local.disk.read", "local.disk.write", "local.disk.maintenance.write"],
    stateReads: ["system.volumes", "storage.disks"],
    stateWrites: ["system.volumes"],
    commands: [
      { commandType: "VOLUME_CREATE", capability: "local.disk.write", payloadSchemaKey: "local.volumeCreate" },
      { commandType: "VOLUME_FORMAT", capability: "local.disk.write", payloadSchemaKey: "local.volumeFormat" },
      { commandType: "DISK_RUN_CHKDSK", capability: "local.disk.maintenance.write", payloadSchemaKey: "local.diskChkDsk" }
    ],
    eventsEmitted: ["simulator.disk.volume.created", "simulator.disk.volume.formatted", "simulator.disk.chkdsk.recorded"],
    targetBindings: ["local.disk_chkdsk_executed"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "msinfo32",
    name: "Systeeminformatie",
    category: "admin",
    capabilities: ["local.system.read"],
    stateReads: ["system"],
    stateWrites: ["system.sysInfoAudit"],
    commands: [
      { commandType: "SYSINFO_RECORD_CHECK", capability: "local.system.read", payloadSchemaKey: "local.sysInfoCheck" }
    ],
    eventsEmitted: ["simulator.sysinfo.checked"],
    targetBindings: ["local.system_info_checked"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "settingsSystem",
    name: "Instellingen Systeem",
    category: "admin",
    capabilities: ["local.system.display.write", "local.system.notifications.write", "local.system.environment.write"],
    stateReads: ["displaySettings", "system.preferences"],
    stateWrites: ["system.preferences"],
    commands: [
      { commandType: "SYSTEM_SET_DISPLAY_SCALING", capability: "local.system.display.write", payloadSchemaKey: "local.systemDisplayScaling" },
      { commandType: "SYSTEM_SET_NOTIFICATIONS", capability: "local.system.notifications.write", payloadSchemaKey: "local.systemNotifications" },
      { commandType: "SYSTEM_SET_ENVIRONMENT_VARIABLE", capability: "local.system.environment.write", payloadSchemaKey: "local.systemEnvironmentVariable" }
    ],
    eventsEmitted: ["simulator.system.display.updated", "simulator.system.notifications.updated", "simulator.system.environment.updated"],
    targetBindings: ["local.display_scaling_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "powercfg",
    name: "Energiebeheer",
    category: "admin",
    capabilities: ["local.system.power.read", "local.system.power.write"],
    stateReads: ["powerPlans", "system.power"],
    stateWrites: ["system.power"],
    commands: [
      { commandType: "POWER_PLAN_SET_ACTIVE", capability: "local.system.power.write", payloadSchemaKey: "local.powerPlanActive" }
    ],
    eventsEmitted: ["simulator.power.plan.updated"],
    targetBindings: ["local.power_plan_active_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "appwiz",
    name: "Programma's en onderdelen",
    category: "admin",
    capabilities: ["local.app.read", "local.app.write"],
    stateReads: ["apps"],
    stateWrites: ["apps"],
    commands: [
      { commandType: "APP_UNINSTALL", capability: "local.app.write", payloadSchemaKey: "local.appUninstall" }
    ],
    eventsEmitted: ["simulator.app.uninstalled"],
    targetBindings: ["local.app_absent"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "optionalfeatures",
    name: "Windows-onderdelen",
    category: "admin",
    capabilities: ["local.feature.read", "local.feature.write"],
    stateReads: ["features"],
    stateWrites: ["features"],
    commands: [
      { commandType: "FEATURE_ENABLE", capability: "local.feature.write", payloadSchemaKey: "local.featureEnable" },
      { commandType: "FEATURE_DISABLE", capability: "local.feature.write", payloadSchemaKey: "local.featureDisable" }
    ],
    eventsEmitted: ["simulator.feature.enabled", "simulator.feature.disabled"],
    targetBindings: ["local.windows_feature_state_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "windowsUpdate",
    name: "Windows Update",
    category: "admin",
    capabilities: ["local.update.read", "local.update.write"],
    stateReads: ["windowsUpdates", "system.updates"],
    stateWrites: ["windowsUpdates", "system.updates"],
    commands: [
      { commandType: "WINDOWS_UPDATE_INSTALL", capability: "local.update.write", payloadSchemaKey: "local.windowsUpdateInstall" },
      { commandType: "WINDOWS_UPDATE_PAUSE", capability: "local.update.write", payloadSchemaKey: "local.windowsUpdatePause" }
    ],
    eventsEmitted: ["simulator.windows_update.installed", "simulator.windows_update.paused"],
    targetBindings: ["local.update_installed"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "defender",
    name: "Windows Defender",
    category: "security",
    capabilities: ["local.defender.read", "local.defender.write", "local.defender.exclusion.write"],
    stateReads: ["defender", "system.defender"],
    stateWrites: ["defender", "system.defender"],
    commands: [
      { commandType: "DEFENDER_RUN_SCAN", capability: "local.defender.write", payloadSchemaKey: "local.defenderScan" },
      { commandType: "DEFENDER_TOGGLE_REALTIME", capability: "local.defender.write", payloadSchemaKey: "local.defenderRealtime" },
      { commandType: "DEFENDER_ADD_EXCLUSION", capability: "local.defender.exclusion.write", payloadSchemaKey: "local.defenderExclusion" }
    ],
    eventsEmitted: ["simulator.defender.scan.completed", "simulator.defender.realtime.updated", "simulator.defender.exclusion.added"],
    targetBindings: ["local.defender_scan_recorded"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "bitlocker",
    name: "BitLocker-stationsversleuteling",
    category: "security",
    capabilities: ["local.bitlocker.read", "local.bitlocker.write"],
    stateReads: ["bitlocker", "system.bitlocker"],
    stateWrites: ["bitlocker", "system.bitlocker"],
    commands: [
      { commandType: "BITLOCKER_ENABLE", capability: "local.bitlocker.write", payloadSchemaKey: "local.bitlockerEnable" },
      { commandType: "BITLOCKER_DISABLE", capability: "local.bitlocker.write", payloadSchemaKey: "local.bitlockerDisable" }
    ],
    eventsEmitted: ["simulator.bitlocker.enabled", "simulator.bitlocker.disabled"],
    targetBindings: ["local.bitlocker_enabled"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "cmd",
    name: "Opdrachtprompt",
    category: "admin",
    capabilities: ["local.shell.execute", "file.folder.write", "file.file.write", "filesystem.write"],
    stateReads: ["system.shellAudit"],
    stateWrites: ["system.shellAudit", "filesystem.nodes"],
    commands: [
      { commandType: "SHELL_EXECUTE_COMMAND", capability: "local.shell.execute", payloadSchemaKey: "local.shellExecuteCommand" },
      { commandType: "FILE_CREATE_FOLDER", capability: "file.folder.write", payloadSchemaKey: "client.fileCreateFolder" },
      { commandType: "FILE_CREATE", capability: "file.file.write", payloadSchemaKey: "client.fileCreate" },
      { commandType: "FILE_WRITE_CONTENT", capability: "file.file.write", payloadSchemaKey: "client.fileWriteContent" },
      { commandType: "FILE_DELETE_FOLDER", capability: "file.folder.write", payloadSchemaKey: "client.fileDeleteFolder" },
      { commandType: "FILE_DELETE", capability: "file.file.write", payloadSchemaKey: "client.fileDelete" },
      { commandType: "FILE_DELETE_FOLDER", capability: "filesystem.write", payloadSchemaKey: "client.fileDeleteFolder" },
      { commandType: "FILE_DELETE", capability: "filesystem.write", payloadSchemaKey: "client.fileDelete" }
    ],
    eventsEmitted: ["simulator.shell.command.recorded", "simulator.file.folder.created", "simulator.file.deleted"],
    targetBindings: ["local.shell_command_executed", "filesystem.folder_exists", "filesystem.file_exists"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "powershell",
    name: "Windows PowerShell",
    category: "admin",
    capabilities: ["local.shell.execute", "file.folder.write", "file.file.write"],
    stateReads: ["system.shellAudit"],
    stateWrites: ["system.shellAudit", "filesystem.nodes"],
    commands: [
      { commandType: "SHELL_EXECUTE_COMMAND", capability: "local.shell.execute", payloadSchemaKey: "local.shellExecuteCommand" },
      { commandType: "FILE_CREATE_FOLDER", capability: "file.folder.write", payloadSchemaKey: "client.fileCreateFolder" },
      { commandType: "FILE_CREATE", capability: "file.file.write", payloadSchemaKey: "client.fileCreate" },
      { commandType: "FILE_WRITE_CONTENT", capability: "file.file.write", payloadSchemaKey: "client.fileWriteContent" },
      { commandType: "FILE_DELETE_FOLDER", capability: "file.folder.write", payloadSchemaKey: "client.fileDeleteFolder" },
      { commandType: "FILE_DELETE", capability: "file.file.write", payloadSchemaKey: "client.fileDelete" }
    ],
    eventsEmitted: ["simulator.shell.command.recorded", "simulator.file.folder.created", "simulator.file.deleted"],
    targetBindings: ["local.shell_command_executed", "filesystem.folder_exists", "filesystem.file_exists"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "notepad",
    name: "Kladblok",
    category: "productivity",
    capabilities: ["file.file.write"],
    stateReads: ["filesystem.nodes"],
    stateWrites: ["filesystem.nodes"],
    commands: [
      { commandType: "FILE_CREATE", capability: "file.file.write", payloadSchemaKey: "file.create" },
      { commandType: "FILE_WRITE_CONTENT", capability: "file.file.write", payloadSchemaKey: "file.writeContent" }
    ],
    eventsEmitted: ["simulator.file.created", "simulator.file.content.updated"],
    targetBindings: ["file.file_exists", "file.file_content_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "controlPrinters",
    name: "Apparaten en printers",
    category: "admin",
    capabilities: ["local.printer.read", "local.printer.write"],
    stateReads: ["printers"],
    stateWrites: ["printers"],
    commands: [
      { commandType: "PRINTER_ADD", capability: "local.printer.write", payloadSchemaKey: "local.printerAdd" },
      { commandType: "PRINTER_REMOVE", capability: "local.printer.write", payloadSchemaKey: "local.printerRemove" },
      { commandType: "PRINTER_SET_DEFAULT", capability: "local.printer.write", payloadSchemaKey: "local.printerSetDefault" }
    ],
    eventsEmitted: ["simulator.printer.added", "simulator.printer.removed", "simulator.printer.default_set"],
    targetBindings: ["local.printer_exists", "local.printer_default_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "ncpa",
    name: "Netwerkverbindingen",
    category: "network",
    capabilities: ["network.adapter.write", "network.dns.write", "network.dhcp.write"],
    stateReads: ["network.adapters", "network.clientDns"],
    stateWrites: ["network.adapters", "network.clientDns"],
    commands: [
      { commandType: "NETWORK_SET_ADAPTER_CONFIG", capability: "network.adapter.write", payloadSchemaKey: "network.adapterConfig" },
      { commandType: "NETWORK_ENABLE_DHCP", capability: "network.adapter.write", payloadSchemaKey: "network.enableDhcp" },
      { commandType: "NETWORK_SET_DNS_SERVER", capability: "network.dns.write", payloadSchemaKey: "network.setDnsServer" },
      { commandType: "NETWORK_RELEASE_RENEW_LEASE", capability: "network.dhcp.write", payloadSchemaKey: "network.dhcpRenew" }
    ],
    eventsEmitted: ["simulator.network.adapter.updated", "simulator.network.lease.renewed", "simulator.network.dns.updated"],
    targetBindings: ["network.adapter_dns_matches", "network.dhcp_lease_received"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "rebootSimulator",
    name: "Systeem herstarten",
    category: "admin",
    capabilities: ["local.system.write"],
    stateReads: ["system.lastRebootAt"],
    stateWrites: ["system.lastRebootAt"],
    commands: [
      { commandType: "SYSTEM_REBOOT", capability: "local.system.write", payloadSchemaKey: "local.systemReboot" }
    ],
    eventsEmitted: ["simulator.system.rebooted"],
    targetBindings: ["local.system_rebooted"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "login",
    name: "Aanmeldscherm",
    category: "security",
    capabilities: ["session.write"],
    stateReads: ["session.loginAudit", "system.hostname", "server.activeDirectory.users"],
    stateWrites: ["session.loginAudit", "events"],
    commands: [
      { commandType: "SESSION_LOGIN_ATTEMPT", capability: "session.write", payloadSchemaKey: "session.loginAttempt" }
    ],
    eventsEmitted: ["simulator.session.login.succeeded", "simulator.session.login.failed"],
    targetBindings: ["identity.domain_login_attempted_succeeded"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "entraAdmin",
    name: "Microsoft Entra Admin Center",
    category: "admin",
    capabilities: ["entra.user.write", "entra.group.write", "entra.license.write", "entra.role.write"],
    stateReads: ["cloud.entra.users", "cloud.entra.groups", "cloud.entra.licenses", "cloud.entra.roleAssignments"],
    stateWrites: ["cloud.entra.users", "cloud.entra.groups", "cloud.entra.licenses", "cloud.entra.roleAssignments"],
    commands: [
      { commandType: "ENTRA_USER_CREATE", capability: "entra.user.write", payloadSchemaKey: "entra.userCreate" },
      { commandType: "ENTRA_GROUP_CREATE", capability: "entra.group.write", payloadSchemaKey: "entra.groupCreate" },
      { commandType: "ENTRA_ASSIGN_LICENSE", capability: "entra.license.write", payloadSchemaKey: "entra.licenseAssign" },
      { commandType: "ENTRA_ROLE_ASSIGN", capability: "entra.role.write", payloadSchemaKey: "entra.roleAssign" }
    ],
    eventsEmitted: ["simulator.entra.user.created", "simulator.entra.group.created", "simulator.entra.license.assigned", "simulator.entra.role.assigned"],
    targetBindings: ["cloud.entra_user_exists", "cloud.entra_group_exists"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "entraAudit",
    name: "Entra Audit Logs",
    category: "admin",
    capabilities: ["entra.audit.read"],
    stateReads: ["cloud.entra.auditLog"],
    stateWrites: ["cloud.entra.auditLog"],
    commands: [
      { commandType: "ENTRA_AUDIT_RECORD_CHECK", capability: "entra.audit.read", payloadSchemaKey: "entra.auditRecordCheck" }
    ],
    eventsEmitted: ["simulator.entra.audit.checked"],
    targetBindings: ["cloud.entra_audit_checked"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "calc",
    name: "Rekenmachine",
    category: "productivity",
    capabilities: ["local.utility.read", "client.utilities.launch", "client.utilities.check"],
    stateReads: ["system.utilityAudit.calc"],
    stateWrites: ["system.utilityAudit.calc", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.utilities.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.utilities.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "CALC_RECORD_RESULT", capability: "local.utility.read", payloadSchemaKey: "local.calcRecordResult" }
    ],
    eventsEmitted: ["simulator.calc.opened", "simulator.calc.checked", "simulator.calc.result.recorded"],
    targetBindings: ["simulator.calc.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "paint",
    name: "Paint",
    category: "productivity",
    capabilities: ["local.utility.write", "client.utilities.launch", "client.utilities.check"],
    stateReads: ["filesystem.nodes", "system.utilityAudit.paint"],
    stateWrites: ["filesystem.nodes", "system.utilityAudit.paint", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.utilities.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.utilities.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "PAINT_SAVE_FILE", capability: "local.utility.write", payloadSchemaKey: "local.paintSaveFile" }
    ],
    eventsEmitted: ["simulator.paint.opened", "simulator.paint.checked", "simulator.paint.file.saved"],
    targetBindings: ["simulator.paint.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "snippingTool",
    name: "Knipprogramma",
    category: "productivity",
    capabilities: ["local.evidence.write", "client.utilities.launch", "client.utilities.check"],
    stateReads: ["events", "system.utilityAudit.snippingTool"],
    stateWrites: ["events", "system.utilityAudit.snippingTool"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.utilities.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.utilities.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "SCREENSHOT_RECORD", capability: "local.evidence.write", payloadSchemaKey: "local.screenshotRecord" }
    ],
    eventsEmitted: ["simulator.snippingTool.opened", "simulator.snippingTool.checked", "simulator.snippingTool.evidence.recorded"],
    targetBindings: ["simulator.snippingTool.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "clipboardHistory",
    name: "Clipboard History",
    category: "productivity",
    capabilities: ["local.clipboard.read", "client.utilities.launch", "client.utilities.check"],
    stateReads: ["clipboard", "system.utilityAudit.clipboardHistory"],
    stateWrites: ["system.utilityAudit.clipboardHistory", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.utilities.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.utilities.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "CLIPBOARD_RECORD_VIEW", capability: "local.clipboard.read", payloadSchemaKey: "local.clipboardRecordView" }
    ],
    eventsEmitted: ["simulator.clipboardHistory.opened", "simulator.clipboardHistory.checked", "simulator.clipboardHistory.view.recorded"],
    targetBindings: ["simulator.clipboardHistory.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "regedit",
    name: "Register-editor",
    category: "admin",
    capabilities: ["local.registry.write", "client.registry.launch", "client.registry.check"],
    stateReads: ["system.registry"],
    stateWrites: ["system.registry", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.registry.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.registry.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "REGISTRY_SET_VALUE", capability: "local.registry.write", payloadSchemaKey: "local.registrySetValue" },
      { commandType: "REGISTRY_DELETE_VALUE", capability: "local.registry.write", payloadSchemaKey: "local.registryDeleteValue" }
    ],
    eventsEmitted: ["simulator.regedit.opened", "simulator.regedit.checked", "simulator.registry.value.updated"],
    targetBindings: ["simulator.regedit.ready", "local.registry_value_matches"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "credentialManager",
    name: "Credential Manager",
    category: "security",
    capabilities: ["local.credential.write", "client.credentials.launch", "client.credentials.check"],
    stateReads: ["system.credentials", "system.utilityAudit.credentialManager"],
    stateWrites: ["system.credentials", "system.utilityAudit.credentialManager", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.credentials.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.credentials.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "CREDENTIAL_ADD", capability: "local.credential.write", payloadSchemaKey: "local.credentialAdd" },
      { commandType: "CREDENTIAL_DELETE", capability: "local.credential.write", payloadSchemaKey: "local.credentialDelete" }
    ],
    eventsEmitted: ["simulator.credentialManager.opened", "simulator.credentialManager.checked", "simulator.credential.added", "simulator.credential.deleted"],
    targetBindings: ["simulator.credentialManager.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "certmgr",
    name: "Certificate Manager",
    category: "security",
    capabilities: ["local.cert.user.write", "client.certificates.launch", "client.certificates.check"],
    stateReads: ["system.certificates.user", "system.utilityAudit.certmgr"],
    stateWrites: ["system.certificates.user", "system.utilityAudit.certmgr", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.certificates.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.certificates.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "CERTIFICATE_ADD_USER_STORE", capability: "local.cert.user.write", payloadSchemaKey: "local.certificateAddUserStore" }
    ],
    eventsEmitted: ["simulator.certmgr.opened", "simulator.certmgr.checked", "simulator.certificate.user.added"],
    targetBindings: ["simulator.certmgr.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "certlm",
    name: "Certificates - Local Computer",
    category: "security",
    capabilities: ["local.cert.machine.write", "client.certificates.launch", "client.certificates.check"],
    stateReads: ["system.certificates.machine", "system.utilityAudit.certlm"],
    stateWrites: ["system.certificates.machine", "system.utilityAudit.certlm", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.certificates.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.certificates.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "CERTIFICATE_ADD_LOCAL_MACHINE_STORE", capability: "local.cert.machine.write", payloadSchemaKey: "local.certificateAddMachineStore" }
    ],
    eventsEmitted: ["simulator.certlm.opened", "simulator.certlm.checked", "simulator.certificate.machine.added"],
    targetBindings: ["simulator.certlm.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "diskCleanup",
    name: "Schijfopruiming",
    category: "productivity",
    capabilities: ["local.disk.maintenance.write", "client.maintenance.launch", "client.maintenance.check"],
    stateReads: ["system.maintenance", "system.utilityAudit.diskCleanup"],
    stateWrites: ["system.maintenance", "system.utilityAudit.diskCleanup", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.maintenance.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.maintenance.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "DISK_CLEANUP_RUN", capability: "local.disk.maintenance.write", payloadSchemaKey: "local.diskCleanupRun" }
    ],
    eventsEmitted: ["simulator.diskCleanup.opened", "simulator.diskCleanup.checked", "simulator.disk.cleanup.recorded"],
    targetBindings: ["simulator.diskCleanup.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "systemRestore",
    name: "Systeemherstel",
    category: "admin",
    capabilities: ["local.system.restore.write", "client.restore.launch", "client.restore.check"],
    stateReads: ["restorePoints", "system.utilityAudit.systemRestore"],
    stateWrites: ["restorePoints", "system.utilityAudit.systemRestore", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.restore.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.restore.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "RESTORE_POINT_CREATE", capability: "local.system.restore.write", payloadSchemaKey: "local.restorePointCreate" }
    ],
    eventsEmitted: ["simulator.systemRestore.opened", "simulator.systemRestore.checked", "simulator.restore_point.created"],
    targetBindings: ["simulator.systemRestore.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "reliabilityMonitor",
    name: "Betrouwbaarheidscontrole",
    category: "admin",
    capabilities: ["local.reliability.read", "client.diagnostics.launch", "client.diagnostics.check"],
    stateReads: ["events", "system.utilityAudit.reliabilityMonitor"],
    stateWrites: ["system.utilityAudit.reliabilityMonitor", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.diagnostics.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.diagnostics.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "RELIABILITY_RECORD_CHECK", capability: "local.reliability.read", payloadSchemaKey: "local.reliabilityRecordCheck" }
    ],
    eventsEmitted: ["simulator.reliabilityMonitor.opened", "simulator.reliabilityMonitor.checked", "simulator.reliability.recorded"],
    targetBindings: ["simulator.reliabilityMonitor.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "resourceMonitor",
    name: "Resource Monitor",
    category: "admin",
    capabilities: ["local.resource.read", "client.diagnostics.launch", "client.diagnostics.check"],
    stateReads: ["system.processes", "server.services", "network.adapters", "system.utilityAudit.resourceMonitor"],
    stateWrites: ["system.utilityAudit.resourceMonitor", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "client.diagnostics.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "client.diagnostics.check", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "RESOURCE_RECORD_CHECK", capability: "local.resource.read", payloadSchemaKey: "local.resourceRecordCheck" }
    ],
    eventsEmitted: ["simulator.resourceMonitor.opened", "simulator.resourceMonitor.checked", "simulator.resource.recorded"],
    targetBindings: ["simulator.resourceMonitor.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "adSites",
    name: "Active Directory Sites and Services",
    category: "server",
    capabilities: ["server.activeDirectory.sites.write", "server.activeDirectory.sites.read", "server.activeDirectory.launch"],
    stateReads: ["server.activeDirectory.sites"],
    stateWrites: ["server.activeDirectory.sites", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.activeDirectory.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.activeDirectory.sites.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "AD_SITE_CREATE", capability: "server.activeDirectory.sites.write", payloadSchemaKey: "server.adSiteCreate" },
      { commandType: "AD_SITELINK_CREATE", capability: "server.activeDirectory.sites.write", payloadSchemaKey: "server.adSiteLinkCreate" }
    ],
    eventsEmitted: ["simulator.adSites.opened", "simulator.ad.site.created", "simulator.ad.site_link.created"],
    targetBindings: ["simulator.adSites.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "adTrusts",
    name: "Active Directory Domains and Trusts",
    category: "server",
    capabilities: ["server.activeDirectory.trusts.write", "server.activeDirectory.trusts.read", "server.activeDirectory.launch"],
    stateReads: ["server.activeDirectory.trusts"],
    stateWrites: ["server.activeDirectory.trusts", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.activeDirectory.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.activeDirectory.trusts.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "AD_TRUST_CREATE", capability: "server.activeDirectory.trusts.write", payloadSchemaKey: "server.adTrustCreate" }
    ],
    eventsEmitted: ["simulator.adTrusts.opened", "simulator.ad.trust.created"],
    targetBindings: ["simulator.adTrusts.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "certAuthority",
    name: "Certification Authority",
    category: "server",
    capabilities: ["server.ca.write", "server.ca.read", "server.certificates.launch"],
    stateReads: ["server.ca.issuedCerts"],
    stateWrites: ["server.ca.issuedCerts", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.certificates.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.ca.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "CA_ISSUE_CERTIFICATE", capability: "server.ca.write", payloadSchemaKey: "server.caIssueCertificate" },
      { commandType: "CA_REVOKE_CERTIFICATE", capability: "server.ca.write", payloadSchemaKey: "server.caRevokeCertificate" }
    ],
    eventsEmitted: ["simulator.certAuthority.opened", "simulator.ca.certificate.issued", "simulator.ca.certificate.revoked"],
    targetBindings: ["simulator.certAuthority.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "iisManager",
    name: "IIS Manager",
    category: "server",
    capabilities: ["server.iis.write", "server.iis.read", "server.web.launch"],
    stateReads: ["server.iis.sites", "server.iis.appPools"],
    stateWrites: ["server.iis.sites", "server.iis.appPools", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.web.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.iis.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "IIS_SITE_CREATE", capability: "server.iis.write", payloadSchemaKey: "server.iisSiteCreate" },
      { commandType: "IIS_APP_POOL_CREATE", capability: "server.iis.write", payloadSchemaKey: "server.iisAppPoolCreate" }
    ],
    eventsEmitted: ["simulator.iisManager.opened", "simulator.iis.site.created", "simulator.iis.app_pool.created"],
    targetBindings: ["simulator.iisManager.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "hyperVManager",
    name: "Hyper-V Manager",
    category: "server",
    capabilities: ["server.hyperv.write", "server.hyperv.read", "server.virtualization.launch"],
    stateReads: ["server.hyperv.vms"],
    stateWrites: ["server.hyperv.vms", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.virtualization.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.hyperv.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "HYPERV_VM_CREATE", capability: "server.hyperv.write", payloadSchemaKey: "server.hypervVmCreate" },
      { commandType: "HYPERV_VM_START", capability: "server.hyperv.write", payloadSchemaKey: "server.hypervVmStart" },
      { commandType: "HYPERV_VM_STOP", capability: "server.hyperv.write", payloadSchemaKey: "server.hypervVmStop" }
    ],
    eventsEmitted: ["simulator.hyperVManager.opened", "simulator.hyperv.vm.created", "simulator.hyperv.vm.started", "simulator.hyperv.vm.stopped"],
    targetBindings: ["simulator.hyperVManager.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "printManagement",
    name: "Print Management",
    category: "server",
    capabilities: ["server.print.write", "server.print.read", "server.printing.launch"],
    stateReads: ["server.printServer.printers"],
    stateWrites: ["server.printServer.printers", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.printing.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.print.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "PRINT_SERVER_ADD_PRINTER", capability: "server.print.write", payloadSchemaKey: "server.printServerAddPrinter" },
      { commandType: "PRINT_SERVER_DEPLOY_VIA_GPO", capability: "server.print.write", payloadSchemaKey: "server.printServerDeployViaGpo" }
    ],
    eventsEmitted: ["simulator.printManagement.opened", "simulator.print_server.printer.added", "simulator.print_server.printer.deployed"],
    targetBindings: ["simulator.printManagement.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "dfsManagement",
    name: "DFS Management",
    category: "server",
    capabilities: ["server.dfs.write", "server.dfs.read", "server.files.launch"],
    stateReads: ["server.dfs.namespaces", "server.dfs.replications"],
    stateWrites: ["server.dfs.namespaces", "server.dfs.replications", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.files.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.dfs.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "DFS_NAMESPACE_CREATE", capability: "server.dfs.write", payloadSchemaKey: "server.dfsNamespaceCreate" },
      { commandType: "DFS_REPLICATION_CREATE", capability: "server.dfs.write", payloadSchemaKey: "server.dfsReplicationCreate" }
    ],
    eventsEmitted: ["simulator.dfsManagement.opened", "simulator.dfs.namespace.created", "simulator.dfs.replication.created"],
    targetBindings: ["simulator.dfsManagement.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "npsManager",
    name: "Network Policy Server",
    category: "server",
    capabilities: ["server.nps.write", "server.nps.read", "server.networkPolicy.launch"],
    stateReads: ["server.nps.policies"],
    stateWrites: ["server.nps.policies", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.networkPolicy.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.nps.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "NPS_POLICY_CREATE", capability: "server.nps.write", payloadSchemaKey: "server.npsPolicyCreate" }
    ],
    eventsEmitted: ["simulator.npsManager.opened", "simulator.nps.policy.created"],
    targetBindings: ["simulator.npsManager.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "windowsServerBackup",
    name: "Windows Server Backup",
    category: "server",
    capabilities: ["server.backup.write", "server.backup.read", "server.backup.launch"],
    stateReads: ["server.backup.jobs"],
    stateWrites: ["server.backup.jobs", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.backup.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.backup.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "BACKUP_JOB_RUN", capability: "server.backup.write", payloadSchemaKey: "server.backupJobRun" },
      { commandType: "BACKUP_JOB_SCHEDULE", capability: "server.backup.write", payloadSchemaKey: "server.backupJobSchedule" }
    ],
    eventsEmitted: ["simulator.windowsServerBackup.opened", "simulator.backup.job.run", "simulator.backup.job.scheduled"],
    targetBindings: ["simulator.windowsServerBackup.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "failoverCluster",
    name: "Failover Cluster Manager",
    category: "server",
    capabilities: ["server.cluster.write", "server.cluster.read", "server.cluster.launch"],
    stateReads: ["server.cluster.nodes", "server.cluster.roles"],
    stateWrites: ["server.cluster.nodes", "server.cluster.roles", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.cluster.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.cluster.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "CLUSTER_NODE_ADD", capability: "server.cluster.write", payloadSchemaKey: "server.clusterNodeAdd" },
      { commandType: "CLUSTER_ROLE_MIGRATE", capability: "server.cluster.write", payloadSchemaKey: "server.clusterRoleMigrate" }
    ],
    eventsEmitted: ["simulator.failoverCluster.opened", "simulator.cluster.node.added", "simulator.cluster.role.migrated"],
    targetBindings: ["simulator.failoverCluster.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "fsrmManager",
    name: "File Server Resource Manager",
    category: "server",
    capabilities: ["server.fsrm.write", "server.fsrm.read", "server.files.launch"],
    stateReads: ["server.fsrm.quotas", "server.fsrm.fileScreens"],
    stateWrites: ["server.fsrm.quotas", "server.fsrm.fileScreens", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.files.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.fsrm.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "FSRM_QUOTA_CREATE", capability: "server.fsrm.write", payloadSchemaKey: "server.fsrmQuotaCreate" },
      { commandType: "FSRM_FILE_SCREEN_CREATE", capability: "server.fsrm.write", payloadSchemaKey: "server.fsrmFileScreenCreate" }
    ],
    eventsEmitted: ["simulator.fsrmManager.opened", "simulator.fsrm.quota.created", "simulator.fsrm.file_screen.created"],
    targetBindings: ["simulator.fsrmManager.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  {
    appId: "storageManager",
    name: "Storage Manager",
    category: "server",
    capabilities: ["server.storage.write", "server.storage.read", "server.storage.launch"],
    stateReads: ["server.storage.pools", "server.storage.volumes"],
    stateWrites: ["server.storage.pools", "server.storage.volumes", "events"],
    commands: [
      { commandType: "OPEN_APP", capability: "server.storage.launch", payloadSchemaKey: "simulator.openApp" },
      { commandType: "RECORD_APP_CHECK", capability: "server.storage.read", payloadSchemaKey: "simulator.recordCheck" },
      { commandType: "STORAGE_POOL_CREATE", capability: "server.storage.write", payloadSchemaKey: "server.storagePoolCreate" },
      { commandType: "STORAGE_VOLUME_CREATE", capability: "server.storage.write", payloadSchemaKey: "server.storageVolumeCreate" }
    ],
    eventsEmitted: ["simulator.storageManager.opened", "simulator.storage.pool.created", "simulator.storage.volume.created"],
    targetBindings: ["simulator.storageManager.ready"],
    dependencies: ["simulator-core.windowing", "simulator-core.commandBus"]
  },
  ...buildFase10CloudManifests(),
  ...buildFase10ReadinessManifests()
];

export const migrationSteps = [
  "Users + rollen",
  "Ticketstatussen",
  "Template mapping",
  "Attempt snapshots"
];

export const workspaceSnapshot: V2WorkspaceSnapshot = {
  student: buildStudentWorkspaceSnapshot(demoTickets, simulatorApps, [
    {
      classGroupId: activePublication.classGroupId,
      className: "Servicedesk klas",
      classCode: "FD-SD"
    }
  ]),
  teacher: {
    template: teacherTemplate,
    activePublication,
    liveBoardSession,
    liveBoardRows,
    classes: [
      {
        classGroupId: activePublication.classGroupId,
        className: "Klas Talland ICT",
        classCode: "TL-ICT-01",
        studentCount: 1,
        publicationCount: 1,
        activePublicationCount: 1,
        attempts: liveBoardRows.map((row) => ({
          attemptId: row.attemptId,
          studentName: row.studentName,
          ticketNumber: row.ticketNumber,
          ticketTitle: row.ticketTitle,
          status: row.status
        }))
      }
    ],
    tickets: demoTickets
  },
  admin: {
    template: teacherTemplate,
    simulatorApps,
    migrationSteps,
    manifestCount: simulatorApps.length,
    activeManifestCount: simulatorApps.length,
    recentTemplates: [
      {
        id: teacherTemplate.id,
        title: teacherTemplate.title,
        topic: teacherTemplate.topic,
        complexity: teacherTemplate.complexity,
        status: teacherTemplate.status,
        updatedLabel: "Vandaag"
      }
    ]
  }
};
